import { memo, useCallback, useMemo, MouseEvent } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { useAlert } from '@frontend/design-system';
import PoundSign from '../../assets/svg/pound.svg';
import { ChatListItemTrackingIds } from '../../constants';
import { useTeamChatSelector } from '../../providers/team-chat.provider';
import { ChatAvatar } from '../common/chat-avatar/chat-avatar';
import { TypingAnimation } from '../common/typing-animation';
import { NavItem } from '../nav/nav-item';

interface ChatListItemProps {
  conversationId: string;
}

export const ConversationListItem = memo(({ conversationId }: ChatListItemProps) => {
  const { success, error } = useAlert();
  const { t } = useTranslation('team-chat');

  const conversation = useTeamChatSelector((ctx) => ctx.helpers.getConversation(conversationId));
  const users = useTeamChatSelector((ctx) => ctx.users);
  const activeConversationId = useTeamChatSelector((ctx) => ctx.activeConversationId);
  const openStaticConversation = useTeamChatSelector((ctx) => ctx.openStaticConversation);
  const clearIsNavigatingFromThreads = useTeamChatSelector((ctx) => ctx.clearIsNavigatingFromThreads);
  const isHideConversationEnabled = useTeamChatSelector((ctx) => ctx.isHideConversationEnabled);
  const updateConversation = useTeamChatSelector((ctx) => ctx.updateConversation);
  const { markConversationAsHidden } = useTeamChatSelector((ctx) => ctx.cache);

  const memberIds = conversation?.memberIds ?? [];

  const members = useMemo(
    () => memberIds.map((memberId) => users?.find((user) => user.userID === memberId)).filter((user) => !!user),
    [memberIds, users]
  );

  const openConversation = useCallback(() => {
    if (!conversation?.channelId) {
      return;
    }
    clearIsNavigatingFromThreads();
    openStaticConversation(conversation.channelId);
  }, [openStaticConversation, conversation?.channelId, clearIsNavigatingFromThreads]);

  const onClickHideConversation = useCallback(
    (e?: MouseEvent<HTMLButtonElement>) => {
      e?.stopPropagation();
      return updateConversation({ conversationId, updates: { isHidden: true } })
        .then(() => {
          success(t('Conversation hidden successfully.'));
          markConversationAsHidden(conversationId, conversation?.type);
        })
        .catch(() => error(t('Unable to hide conversation at the moment'))) as Promise<void>;
    },
    [conversationId, updateConversation, conversation?.type, t, success, error]
  );

  const Icon = useMemo(() => {
    return conversation?.usersTyping.length && conversation.usersTyping.length > 0 ? (
      <TypingAnimation />
    ) : conversation?.type === 'DM' ? (
      <ChatAvatar users={members} />
    ) : (
      <img src={PoundSign} css={poundSignStyle} alt='#' />
    );
  }, [conversation?.usersTyping.length, conversation?.type, members]);

  if (!conversation || conversation.isHidden) {
    return null;
  }

  const isSelected = activeConversationId === conversation?.channelId;
  const trackingId =
    conversation?.type === 'DM'
      ? conversation.memberIds.length === 2
        ? ChatListItemTrackingIds.DM
        : ChatListItemTrackingIds.GROUP_DM
      : ChatListItemTrackingIds.CHANNEL;

  return (
    <NavItem
      isSelected={isSelected}
      onClick={openConversation}
      trackingId={trackingId}
      label={conversation.name}
      Icon={Icon}
      badgeCount={conversation.unreadCount}
      shouldShowHideButton={isHideConversationEnabled}
      onClickHideConversation={onClickHideConversation}
      hideButtonTrackingId={
        conversation.type === 'Group' ? 'team-chat-2.0-hide-channel-button' : 'team-chat-2.0-hide-dm-button'
      }
    />
  );
});
ConversationListItem.displayName = 'ConversationListItem';

const poundSignStyle = css({
  width: '24px',
  height: '24px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'visible',
});
